// src/utils/ocrReceiptScanner.js
// ============================================================================
// Client-side OCR receipt scanning using Tesseract.js (no external API needed).
//
// Accepts a File or Blob and returns extracted fields:
//   amount, currency, date, vendor, description, category, confidence, rawText
// ============================================================================

import Tesseract from 'tesseract.js';

// Category keywords for auto-classification
const CATEGORY_KEYWORDS = {
  'Travel Costs': ['uber', 'ola', 'taxi', 'cab', 'flight', 'airline', 'train', 'metro', 'bus', 'toll', 'parking', 'fuel', 'petrol', 'diesel', 'grab', 'lyft', 'rickshaw', 'auto'],
  'Food & Beverages': ['restaurant', 'cafe', 'coffee', 'starbucks', 'zomato', 'swiggy', 'food', 'lunch', 'dinner', 'breakfast', 'meal', 'tea', 'snack', 'canteen', 'catering'],
  Materials: ['stationery', 'paper', 'print', 'office supplies', 'ink', 'toner', 'pen', 'material', 'art supply', 'model', 'foam', 'cardboard'],
  Courier: ['courier', 'dhl', 'fedex', 'post', 'shipping', 'delivery', 'bluedart', 'dtdc', 'speed post'],
  Hardware: ['hardware', 'laptop', 'mouse', 'keyboard', 'monitor', 'cable', 'charger', 'adapter', 'electronics', 'phone'],
  Software: ['software', 'license', 'subscription', 'adobe', 'autodesk', 'figma', 'saas', 'plugin', 'domain', 'hosting'],
};

// Euro indicators — the € sign, the ISO code, or the word "euro" anywhere in the bill.
const EURO_PATTERNS = [/€/, /\bEUR\b/i, /\beuros?\b/i];

/**
 * Determine the receipt currency.
 *
 * BRIO receipts are almost always either EUR (Netherlands) or INR (India):
 *   - If the bill mentions euro anywhere (€, EUR, or the word "euro") → EUR.
 *   - Otherwise default to INR. Indian bills frequently omit any currency
 *     sign, so absence of a euro mention is treated as INR rather than unknown.
 *
 * Always returns 'EUR' or 'INR' (never null) so downstream office mapping is
 * always populated.
 */
function detectReceiptCurrency(text) {
  for (const pat of EURO_PATTERNS) {
    if (pat.test(text)) return 'EUR';
  }
  return 'INR';
}

/**
 * Parse a number string that may use European (1.234,56), US/UK (1,234.56) or
 * Indian (1,00,000.00) grouping. Returns a float or NaN.
 *
 * The last separator is treated as a decimal point only when it is followed by
 * 1–2 digits. A trailing group of exactly 3 digits (e.g. "1,00,000" or the
 * European "1.234") means every separator is a grouping separator, so there is
 * no decimal part. This avoids misreading Indian lakh-grouped amounts, where an
 * unqualified "comma-is-decimal" heuristic would turn "1,00,000" into 1.00.
 */
function parseFlexibleNumber(str) {
  if (!str) return NaN;
  const cleaned = str.replace(/[^\d.,]/g, '');
  if (!cleaned) return NaN;

  const lastComma = cleaned.lastIndexOf(',');
  const lastDot = cleaned.lastIndexOf('.');
  const lastSep = Math.max(lastComma, lastDot);

  if (lastSep === -1) return parseFloat(cleaned);

  const digitsAfter = cleaned.length - lastSep - 1;
  const lastSepIsDecimal = digitsAfter >= 1 && digitsAfter <= 2;

  if (lastSepIsDecimal) {
    const intPart = cleaned.slice(0, lastSep).replace(/[.,]/g, '');
    const fracPart = cleaned.slice(lastSep + 1);
    return parseFloat(`${intPart}.${fracPart}`);
  }
  // No decimal part — all separators are grouping separators.
  return parseFloat(cleaned.replace(/[.,]/g, ''));
}

function extractAmount(text) {
  const lines = text.split('\n').map(l => l.trim()).filter(Boolean);

  // The bill total is usually written to the right of a label. On Indian bills
  // that label is typically "Gross Amt"/"Gross Amount"/"Total"; on Dutch (NL)
  // bills it is "Totaal"/"Total". \b before the label keeps "Subtotal" from
  // matching "total". An optional Rs./INR/€ prefix may sit before the number.
  const totalPatterns = [
    /\b(?:grand\s*total|gross\s*am(?:oun)?t|net\s*(?:amount|payable)|amount\s*(?:due|payable)|balance\s*due|bill\s*amount|totaal|total)\b\s*[:=]?\s*(?:[₹€$]|rs\.?|inr|eur)?\s*([\d.,]+)/i,
    /[₹€$]\s*([\d.,]+)\s*(?:total|due|payable)/i,
  ];

  for (const pat of totalPatterns) {
    for (const line of lines) {
      const m = line.match(pat);
      if (m) {
        const amt = parseFlexibleNumber(m[1]);
        if (amt > 0 && amt < 10_000_000) {
          return { amount: amt };
        }
      }
    }
  }

  // Fallback: find the largest currency-prefixed amount
  const amountRegex = /[₹€$]?\s*([\d.,]+)\b/g;
  let best = null;

  for (const line of lines) {
    let match;
    while ((match = amountRegex.exec(line)) !== null) {
      const val = parseFlexibleNumber(match[0]);
      if (val > 0 && val < 10_000_000 && (best === null || val > best)) {
        best = val;
      }
    }
  }

  return { amount: best };
}

function extractDate(text) {
  const datePatterns = [
    // DD/MM/YYYY or DD-MM-YYYY
    {
      regex: /(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{4})/,
      parse: (m) => {
        const day = parseInt(m[1]), month = parseInt(m[2]), year = parseInt(m[3]);
        if (month >= 1 && month <= 12 && day >= 1 && day <= 31 && year >= 2020 && year <= 2030) {
          return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        }
        return null;
      },
    },
    // YYYY-MM-DD
    {
      regex: /(\d{4})-(\d{2})-(\d{2})/,
      parse: (m) => {
        const year = parseInt(m[1]), month = parseInt(m[2]), day = parseInt(m[3]);
        if (month >= 1 && month <= 12 && day >= 1 && day <= 31 && year >= 2020 && year <= 2030) {
          return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        }
        return null;
      },
    },
    // DD Mon YYYY (e.g., 22 Mar 2026)
    {
      regex: /(\d{1,2})\s+(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\w*\s+(\d{4})/i,
      parse: (m) => {
        const months = { jan: 1, feb: 2, mar: 3, apr: 4, may: 5, jun: 6, jul: 7, aug: 8, sep: 9, oct: 10, nov: 11, dec: 12 };
        const day = parseInt(m[1]);
        const month = months[m[2].toLowerCase().substring(0, 3)];
        const year = parseInt(m[3]);
        if (month && day >= 1 && day <= 31 && year >= 2020 && year <= 2030) {
          return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        }
        return null;
      },
    },
  ];

  const lines = text.split('\n');
  for (const line of lines) {
    for (const { regex, parse } of datePatterns) {
      const m = line.match(regex);
      if (m) {
        const result = parse(m);
        if (result) return result;
      }
    }
  }
  return null;
}

// Lines near the top of a bill that are NOT the vendor name: dates/addresses,
// pure amounts, phone/fax numbers, emails, websites, tax/registration IDs and
// common header labels.
const NON_VENDOR_LINE = [
  /^\d+[/\-.]/,                                // starts like a date / address number
  /^\d+\s+[A-Za-z]/,                           // street address (e.g. "123 Main Street")
  /^[₹€$\d,.\s]+$/,                            // pure amount / number line
  /^[+(]?\d[\d\s()/+-]{6,}$/,                  // phone / fax number
  /@/,                                          // email address
  /\b(?:www\.|https?:\/\/)/i,                  // website URL
  /\b(?:gstin|gst\s*no|pan|tin|vat|cin|udyam|hsn|fssai)\b/i, // tax / registration IDs
  /^(?:tax|gst|cgst|sgst|igst|total|subtotal|amount|bill|invoice|receipt|order|date|time|phone|tel|mob|mobile|fax|email|cashier|table)\b/i,
];

function extractVendor(text) {
  const lines = text.split('\n').map(l => l.trim()).filter(Boolean);
  // The vendor / shop name is almost always the most prominent text at the very
  // top of the bill, so scan the top lines and return the first that reads like
  // a name — skipping the contact/ID/header lines that often sit up there too.
  for (const line of lines.slice(0, 6)) {
    if (line.length < 2 || line.length > 60) continue;
    // Strip decorative punctuation (e.g. "*** RECEIPT ***") before classifying.
    const core = line.replace(/^[^A-Za-z0-9]+/, '').replace(/[^A-Za-z0-9]+$/, '');
    if (core.length < 2) continue;
    if (NON_VENDOR_LINE.some((re) => re.test(core))) continue;
    // Require at least two letters so codes and separators aren't mistaken for a name.
    if ((core.match(/[A-Za-z]/g) || []).length < 2) continue;
    return core.replace(/[^\w\s&.'()-]/g, '').trim().substring(0, 60);
  }
  return null;
}

function guessCategory(vendor, text) {
  const searchText = ((vendor || '') + ' ' + text).toLowerCase();
  for (const [category, keywords] of Object.entries(CATEGORY_KEYWORDS)) {
    for (const kw of keywords) {
      if (searchText.includes(kw)) return category;
    }
  }
  return null;
}

function buildDescription(vendor, category, text) {
  const lines = text.split('\n').map(l => l.trim()).filter(Boolean);
  for (const line of lines) {
    if (line.length >= 5 && line.length <= 80) {
      if (/(?:trip|ride|order|delivery|purchase|service|item)/i.test(line)) {
        return line.substring(0, 80);
      }
    }
  }
  return null;
}

function parseReceiptText(rawText) {
  const { amount } = extractAmount(rawText);
  const currency = detectReceiptCurrency(rawText);
  const date = extractDate(rawText);
  const vendor = extractVendor(rawText);
  const category = guessCategory(vendor, rawText);
  const description = buildDescription(vendor, category, rawText);

  let fieldsFound = 0;
  if (amount !== null) fieldsFound++;
  if (date !== null) fieldsFound++;
  if (vendor !== null) fieldsFound++;
  if (category !== null) fieldsFound++;
  const confidence = rawText.length > 0 ? fieldsFound / 4 : 0;

  return { amount, currency, date, vendor, description, category, confidence, rawText };
}

/**
 * Scan a receipt image file using Tesseract.js OCR.
 * @param {File|Blob} file - The image file to scan
 * @param {function} [onProgress] - Optional progress callback (0-1)
 * @returns {Promise<{ok: boolean, result: object, message?: string}>}
 */
export async function scanReceiptFile(file, onProgress) {
  const worker = await Tesseract.createWorker('eng', Tesseract.OEM.DEFAULT, {
    logger: onProgress ? (m) => {
      if (m.status === 'recognizing text' && typeof m.progress === 'number') {
        onProgress(m.progress);
      }
    } : undefined,
  });

  try {
    const { data: { text } } = await worker.recognize(file);

    if (!text || !text.trim()) {
      return {
        ok: true,
        result: { amount: null, currency: null, date: null, vendor: null, description: null, category: null, confidence: 0, rawText: '' },
        message: 'No text detected in the image.',
      };
    }

    const result = parseReceiptText(text);
    return { ok: true, result };
  } finally {
    await worker.terminate();
  }
}
